/**
 * Ethereal Treasure / Racalvin slots audio.
 *
 * Layers (HTMLAudioElement only — no Howler / second stack):
 *  1) Slot machine SFX pack (Documents se_*.wav + win celeb) → /slots/audio/sfx/
 *  2) Optional pirate win overlays (legacy win_*.wav)
 *  3) BGM bandoneon loop
 *  4) Kenney casino as soft fallback if a pack file 404s
 *
 * Settings: same `poker_settings` soundEnabled / soundVolume / music*.
 * @see docs/SLOTS_AUDIO_SSOT.md
 */
import { casino, unlockCasinoAudio } from "./casino-sfx";

const BASE = "/slots/audio";
const SFX = `${BASE}/sfx`;

/**
 * Semantic SSOT paths (stable names). Source hashes kept beside them in /sfx/.
 *
 * | File            | Dur | Role |
 * |-----------------|-----|------|
 * | spin-start      | 2s  | Lever / spin arm |
 * | spin-loop       | 2s  | Looping reel chatter while spinning |
 * | spin-mid        | 4s  | Mid-spin accent (plays ~0.9s in) |
 * | spin-tick       | 1s  | Per-reel land tick |
 * | spin-stop       | 2s  | Final reels stop |
 * | spin-bed        | 10s | Optional long bed (starts with spin; stopped on land) |
 * | win-celeb       | 10s | Normal / big / free / bonus win |
 * | win-jackpot     | 10s | Jackpot / mega |
 */
export const SLOTS_AUDIO = {
  bgm: `${BASE}/bgm-bandoneon-adventure.ogg`,
  /** Sea / voyage ambience — preferred in fullscreen */
  bgmAlt: `${BASE}/bgm-adventure-at-sea.ogg`,
  /** Pirate stingers (always on disk — Racalvin VO falls back here) */
  winPirata: `${BASE}/win_pirata.wav`,
  winEnemy: `${BASE}/win_enemy.wav`,
  winCaptain: `${BASE}/win_captain.wav`,
  /**
   * Use original Document filenames on CDN (content-identical aliases can be
   * deduped by the asset pipeline — originals always ship as unique keys).
   */
  sfx: {
    spinStart: `${SFX}/se_rl74b5xgf6algfi2.wav`,
    spinLoop: `${SFX}/small-casino-slot-machine-ip422bk5.wav`,
    spinMid: `${SFX}/se_h6ygdqv3wsdiz3t2.wav`,
    spinTick: `${SFX}/se_swzrirlrdmcjyvlw.wav`,
    spinStop: `${SFX}/se_6lwfrh6nyzcc7etk.wav`,
    spinBed: `${SFX}/se_ulmgeu6ei7b5mcvp.wav`,
    winCeleb: `${SFX}/7s-slot-machine-win-celeb-z6yi5smx.wav`,
    winJackpot: `${SFX}/se_alj2vah5jydmtbax.wav`,
  },
  /**
   * Optional ElevenLabs Racalvin VO under /voice/*.mp3.
   * When missing, pirate WAVs play so Racalvin always “speaks.”
   */
  voice: {
    welcome: `${BASE}/voice/racalvin-welcome.mp3`,
    bigWin: `${BASE}/voice/racalvin-bigwin.mp3`,
    jackpot: `${BASE}/voice/racalvin-jackpot.mp3`,
    freeSpins: `${BASE}/voice/racalvin-voyage.mp3`,
  },
} as const;

/** Pirate stand-ins when VO files are absent */
const VOICE_PIRATE_FALLBACK: Record<
  keyof typeof SLOTS_AUDIO.voice,
  string
> = {
  welcome: SLOTS_AUDIO.winCaptain,
  bigWin: SLOTS_AUDIO.winEnemy,
  jackpot: SLOTS_AUDIO.winCaptain,
  freeSpins: SLOTS_AUDIO.winPirata,
};

function readSettings(): {
  enabled: boolean;
  volume: number;
  music: boolean;
  musicVol: number;
} {
  try {
    const raw = localStorage.getItem("poker_settings");
    if (raw) {
      const s = JSON.parse(raw) as Record<string, unknown>;
      const enabled = s.soundEnabled !== false;
      const volume = Math.min(
        1,
        Math.max(0, (Number(s.soundVolume) || 75) / 100),
      );
      const music = s.musicEnabled !== false;
      const musicVol = Math.min(
        1,
        Math.max(0, (Number(s.musicVolume) ?? 40) / 100),
      );
      return { enabled, volume, music, musicVol };
    }
  } catch {
    /* ignore */
  }
  return { enabled: true, volume: 0.75, music: true, musicVol: 0.4 };
}

const oneshotPool = new Map<string, HTMLAudioElement>();

function playUrl(
  url: string,
  opts?: { volume?: number; loop?: boolean; restart?: boolean },
): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  const { enabled, volume } = readSettings();
  if (!enabled) return null;
  try {
    let a = oneshotPool.get(url);
    if (!a) {
      a = new Audio(url);
      a.preload = "auto";
      oneshotPool.set(url, a);
    }
    if (opts?.restart !== false) {
      a.pause();
      a.currentTime = 0;
    }
    a.loop = Boolean(opts?.loop);
    a.volume = Math.min(1, Math.max(0, opts?.volume ?? volume));
    void a.play().catch(() => {
      /* autoplay / missing */
    });
    return a;
  } catch {
    return null;
  }
}

function stopUrl(url: string): void {
  const a = oneshotPool.get(url);
  if (!a) return;
  a.pause();
  a.currentTime = 0;
  a.loop = false;
}

let bgmEl: HTMLAudioElement | null = null;
let midSpinTimer: ReturnType<typeof setTimeout> | null = null;
let spinSafetyTimer: ReturnType<typeof setTimeout> | null = null;

export function unlockSlotsAudio(): void {
  unlockCasinoAudio();
  casino.unlock();
  // Warm primary SFX + pirate stingers so first spin / win is not silent
  for (const url of [
    SLOTS_AUDIO.sfx.spinStart,
    SLOTS_AUDIO.sfx.spinLoop,
    SLOTS_AUDIO.sfx.spinTick,
    SLOTS_AUDIO.sfx.spinStop,
    SLOTS_AUDIO.sfx.spinMid,
    SLOTS_AUDIO.sfx.winCeleb,
    SLOTS_AUDIO.sfx.winJackpot,
    SLOTS_AUDIO.winPirata,
    SLOTS_AUDIO.winEnemy,
    SLOTS_AUDIO.winCaptain,
  ]) {
    if (!oneshotPool.has(url)) {
      const a = new Audio(url);
      a.preload = "auto";
      oneshotPool.set(url, a);
    }
  }
}

let bgmMode: "cabinet" | "sea" = "cabinet";

/**
 * Fullscreen prefers sea voyage BGM; cabinet uses bandoneon.
 * Always falls back if a file fails.
 */
export function setSlotsBgmMode(mode: "cabinet" | "sea"): void {
  bgmMode = mode;
  if (!bgmEl) {
    startSlotsBgm();
    return;
  }
  const { musicVol } = readSettings();
  const next = mode === "sea" ? SLOTS_AUDIO.bgmAlt : SLOTS_AUDIO.bgm;
  if (!bgmEl.src.includes(mode === "sea" ? "adventure-at-sea" : "bandoneon")) {
    const t = bgmEl.currentTime;
    bgmEl.src = next;
    bgmEl.loop = true;
    bgmEl.volume =
      mode === "sea"
        ? Math.min(0.48, musicVol)
        : Math.min(0.42, musicVol);
    void bgmEl.play().catch(() => {});
    try {
      bgmEl.currentTime = t;
    } catch {
      /* ignore seek */
    }
  }
}

export function startSlotsBgm(): void {
  if (typeof window === "undefined") return;
  const { enabled, music, musicVol } = readSettings();
  if (!enabled || !music) return;
  unlockSlotsAudio();
  const src = bgmMode === "sea" ? SLOTS_AUDIO.bgmAlt : SLOTS_AUDIO.bgm;
  if (!bgmEl) {
    bgmEl = new Audio(src);
    bgmEl.loop = true;
    bgmEl.preload = "auto";
    bgmEl.volume = Math.min(bgmMode === "sea" ? 0.48 : 0.42, musicVol);
    bgmEl.addEventListener("error", () => {
      if (!bgmEl) return;
      // Cross-fallback between bandoneon and sea
      if (bgmEl.src.indexOf("bgm-bandoneon") >= 0) {
        bgmEl.src = SLOTS_AUDIO.bgmAlt;
      } else if (bgmEl.src.indexOf("adventure-at-sea") >= 0) {
        bgmEl.src = SLOTS_AUDIO.bgm;
      }
      void bgmEl.play().catch(() => {});
    });
  } else if (
    (bgmMode === "sea" && !bgmEl.src.includes("adventure-at-sea")) ||
    (bgmMode === "cabinet" && !bgmEl.src.includes("bandoneon") && !bgmEl.src.includes("adventure-at-sea"))
  ) {
    bgmEl.src = src;
  }
  bgmEl.volume = Math.min(bgmMode === "sea" ? 0.48 : 0.42, musicVol);
  void bgmEl.play().catch(() => {});
}

export function stopSlotsBgm(): void {
  if (!bgmEl) return;
  bgmEl.pause();
  bgmEl.currentTime = 0;
}

export function setSlotsBgmPaused(paused: boolean): void {
  if (!bgmEl) return;
  if (paused) bgmEl.pause();
  else void bgmEl.play().catch(() => {});
}

function clearSpinTimers(): void {
  if (midSpinTimer != null) {
    clearTimeout(midSpinTimer);
    midSpinTimer = null;
  }
  if (spinSafetyTimer != null) {
    clearTimeout(spinSafetyTimer);
    spinSafetyTimer = null;
  }
}

function stopAllSpinLayers(): void {
  clearSpinTimers();
  stopUrl(SLOTS_AUDIO.sfx.spinLoop);
  stopUrl(SLOTS_AUDIO.sfx.spinBed);
  stopUrl(SLOTS_AUDIO.sfx.spinMid);
  stopUrl(SLOTS_AUDIO.sfx.spinStart);
}

/**
 * Spin start — machine start + looping reel noise + mid-spin accent.
 * Spin bed (10s) runs under the loop and is cut when reels land.
 */
export function slotsSpinStart(): void {
  if (typeof window === "undefined") return;
  unlockSlotsAudio();
  const { volume } = readSettings();
  stopAllSpinLayers();

  // Lever / arm
  playUrl(SLOTS_AUDIO.sfx.spinStart, { volume: Math.min(1, volume * 0.95) });
  // Soft Kenney underlay if pack fails
  casino.spinStart();

  // 2s cabinet loop — primary spin chatter
  playUrl(SLOTS_AUDIO.sfx.spinLoop, {
    volume: Math.min(1, volume * 0.55),
    loop: true,
  });
  // 10s bed under the loop (richer cabinet for longer spins)
  playUrl(SLOTS_AUDIO.sfx.spinBed, {
    volume: Math.min(1, volume * 0.38),
    loop: false,
  });

  // Mid-spin accent at ~0.9s (4s file covers mid window)
  midSpinTimer = setTimeout(() => {
    playUrl(SLOTS_AUDIO.sfx.spinMid, {
      volume: Math.min(1, volume * 0.7),
    });
  }, 900);

  // Safety stop if client never lands (~12s)
  spinSafetyTimer = setTimeout(() => {
    stopAllSpinLayers();
  }, 12000);
}

/** One reel lands — short tick (call from AnimatedReel if desired) */
export function slotsReelTick(): void {
  const { volume } = readSettings();
  playUrl(SLOTS_AUDIO.sfx.spinTick, {
    volume: Math.min(1, volume * 0.75),
  });
}

/** All reels done spinning */
export function slotsSpinStop(): void {
  stopAllSpinLayers();
  const { volume } = readSettings();
  playUrl(SLOTS_AUDIO.sfx.spinStop, {
    volume: Math.min(1, volume * 0.9),
  });
  casino.spinStop();
}

export function slotsLever(): void {
  unlockSlotsAudio();
  const { volume } = readSettings();
  playUrl(SLOTS_AUDIO.sfx.spinStart, { volume: Math.min(1, volume * 0.85) });
  casino.bet();
}

/**
 * Racalvin VO with pirate WAV fallback (voice/*.mp3 optional).
 */
export function slotsRacalvinVoice(
  kind: keyof typeof SLOTS_AUDIO.voice,
  volumeScale = 0.9,
): void {
  unlockSlotsAudio();
  const { volume } = readSettings();
  const primary = SLOTS_AUDIO.voice[kind];
  const fallback = VOICE_PIRATE_FALLBACK[kind];
  const vol = Math.min(1, volume * volumeScale);

  try {
    let a = oneshotPool.get(primary);
    if (!a) {
      a = new Audio(primary);
      a.preload = "auto";
      oneshotPool.set(primary, a);
    }
    const onErr = () => {
      a?.removeEventListener("error", onErr);
      playUrl(fallback, { volume: vol });
    };
    a.addEventListener("error", onErr, { once: true });
    a.pause();
    a.currentTime = 0;
    a.volume = vol;
    void a.play().catch(() => {
      playUrl(fallback, { volume: vol });
    });
  } catch {
    playUrl(fallback, { volume: vol });
  }
}

/** Explicit pirate stinger (crew cheer / threat / captain) */
export function slotsPirate(
  kind: "pirata" | "enemy" | "captain",
  volumeScale = 0.4,
): void {
  unlockSlotsAudio();
  const { volume } = readSettings();
  const url =
    kind === "enemy"
      ? SLOTS_AUDIO.winEnemy
      : kind === "captain"
        ? SLOTS_AUDIO.winCaptain
        : SLOTS_AUDIO.winPirata;
  playUrl(url, { volume: Math.min(1, volume * volumeScale) });
}

/**
 * Win stinger:
 * - jackpot → win-jackpot pack + captain + Racalvin VO
 * - big / free / bonus / normal → win-celeb + pirate layer + VO
 */
export function slotsWin(
  kind: "normal" | "big" | "jackpot" | "free" | "bonus",
): void {
  unlockSlotsAudio();
  const { volume } = readSettings();
  stopAllSpinLayers();

  if (kind === "jackpot") {
    playUrl(SLOTS_AUDIO.sfx.winJackpot, {
      volume: Math.min(1, volume * 1.0),
    });
    slotsPirate("captain", 0.45);
    slotsRacalvinVoice("jackpot", 0.9);
    casino.blackjack();
    return;
  }

  const celebVol =
    kind === "big"
      ? Math.min(1, volume * 0.95)
      : kind === "free" || kind === "bonus"
        ? Math.min(1, volume * 0.88)
        : Math.min(1, volume * 0.82);
  playUrl(SLOTS_AUDIO.sfx.winCeleb, { volume: celebVol });

  if (kind === "big") {
    slotsPirate("enemy", 0.45);
    slotsRacalvinVoice("bigWin", 0.85);
    casino.blackjack();
  } else if (kind === "free" || kind === "bonus") {
    slotsPirate("pirata", 0.4);
    slotsRacalvinVoice("freeSpins", 0.85);
    casino.win();
  } else {
    slotsPirate("pirata", 0.32);
    casino.win();
  }
}

export function slotsLoseTick(): void {
  const { volume } = readSettings();
  playUrl(SLOTS_AUDIO.sfx.spinTick, { volume: Math.min(1, volume * 0.45) });
  casino.bet();
}

export function slotsWelcomeVoice(): void {
  slotsRacalvinVoice("welcome", 0.92);
  // Soft pirate under-cheer so welcome always has body even if VO is thin
  window.setTimeout(() => slotsPirate("captain", 0.22), 180);
}

export const slotsAudio = {
  unlock: unlockSlotsAudio,
  startBgm: startSlotsBgm,
  stopBgm: stopSlotsBgm,
  pauseBgm: setSlotsBgmPaused,
  setBgmMode: setSlotsBgmMode,
  spinStart: slotsSpinStart,
  spinStop: slotsSpinStop,
  reelTick: slotsReelTick,
  lever: slotsLever,
  win: slotsWin,
  lose: slotsLoseTick,
  welcome: slotsWelcomeVoice,
  voice: slotsRacalvinVoice,
  pirate: slotsPirate,
  assets: SLOTS_AUDIO,
};
